import qs from 'qs';

import { fetcher } from './fetcher';
import { createTimeoutSignal } from './timeout-signal';

type ApiClientRequestOptions = {
  headers?: Record<string, string>;
  cache?: RequestInit['cache'];
  signal?: AbortSignal;
  next?: {
    revalidate?: number;
    tags?: string[];
  };
};

export type RequestConfig = {
  url: string;
  headers?: Record<string, string>;
  params?: Record<string, string> | string;
  data?: unknown;
};

export class HttpException extends Error {
  constructor(public status: number, public message: string, public data?: unknown) {
    super(message);
  }
}

async function readErrorBody(response: Response): Promise<unknown> {
  try {
    const body = await response.text();
    return body.length > 0 ? JSON.parse(body) : undefined;
  } catch {
    return undefined;
  }
}

type ApiFetchResponse<ResponseBody> = {
  data: ResponseBody;
  timestamp?: number;
  status: number;
  statusText: string;
  headers: Headers;
  requestConfig: RequestConfig;
};

type RequestInterceptor = (request: RequestConfig) => Promise<RequestConfig>;

function normalizeParams(
  params?: Record<string, unknown> | string,
): string | undefined {
  if (!params) {
    return undefined;
  }

  if (typeof params === 'string') {
    return params;
  }

  return qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true });
}

export class ApiClient {
  private requestInterceptors: RequestInterceptor[] = [];

  constructor(private baseUrl: string, private defaultTimeoutMs?: number) { }

  async get<
    ResponseBody = unknown,
    QueryParams extends Record<string, unknown> | undefined = Record<string, unknown>,
  >(
    path: string,
    params?: QueryParams | string,
    options?: ApiClientRequestOptions,
  ): Promise<ApiFetchResponse<ResponseBody>> {
    const buildUrl = new URL(path, this.baseUrl);

    const requestConfig: RequestConfig = {
      url: buildUrl.toString(),
      headers: options?.headers,
      params: params as RequestConfig['params'],
    };

    const resolvedRequestConfig = await this.resolveRequestConfig(requestConfig);

    const response = await fetcher.get(
      resolvedRequestConfig.url,
      normalizeParams(resolvedRequestConfig.params as Record<string, unknown> | string),
      {
        headers: resolvedRequestConfig.headers,
        cache: options?.cache,
        next: options?.next,
        signal: this.resolveSignal(options),
      },
    );

    if (!response.ok) {
      throw new HttpException(response.status, response.statusText, await readErrorBody(response));
    }

    const isBlob = typeof params === 'object'
      && params !== null
      && 'responseType' in params
      && (params as Record<string, unknown>)['responseType'] === 'blob';

    const apiFetchResponse: ApiFetchResponse<ResponseBody> = {
      data: isBlob ? await response.blob() : await response.json(),
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      requestConfig: resolvedRequestConfig,
    };

    return apiFetchResponse;
  }

  async post<ResponseBody = unknown>(
    path: string,
    data: unknown,
    options?: ApiClientRequestOptions,
  ): Promise<ApiFetchResponse<ResponseBody>> {
    const builtUrl = new URL(path, this.baseUrl);

    const requestConfig: RequestConfig = {
      url: builtUrl.toString(),
      headers: options?.headers,
      data,
    };

    const resolvedRequestConfig = await this.resolveRequestConfig(requestConfig);

    const response = await fetcher.post(resolvedRequestConfig.url, resolvedRequestConfig.data, {
      headers: resolvedRequestConfig.headers,
      signal: this.resolveSignal(options),
    });

    if (!response.ok) {
      throw new HttpException(response.status, response.statusText, await readErrorBody(response));
    }

    const responseText = await response.text();
    const responseHasJsonBody = responseText.length > 0;

    const responseJsonOrUndefined: ResponseBody | undefined = responseHasJsonBody
      ? JSON.parse(responseText)
      : undefined;

    const apiFetchResponse: ApiFetchResponse<ResponseBody> = {
      data: responseJsonOrUndefined as ResponseBody,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      requestConfig: resolvedRequestConfig,
    };

    return apiFetchResponse;
  }

  async put<ResponseBody = unknown>(
    path: string,
    data: unknown,
    options?: ApiClientRequestOptions,
  ): Promise<ApiFetchResponse<ResponseBody>> {
    const builtUrl = new URL(path, this.baseUrl);

    const requestConfig: RequestConfig = {
      url: builtUrl.toString(),
      headers: options?.headers,
      data,
    };

    const resolvedRequestConfig = await this.resolveRequestConfig(requestConfig);

    const response = await fetcher.put(
      resolvedRequestConfig.url,
      resolvedRequestConfig.data,
      { headers: resolvedRequestConfig.headers, signal: this.resolveSignal(options) },
    );

    if (!response.ok) {
      throw new HttpException(response.status, response.statusText, await readErrorBody(response));
    }

    const responseText = await response.text();
    const responseHasJsonBody = responseText.length > 0;

    const responseJsonOrUndefined: ResponseBody | undefined = responseHasJsonBody
      ? JSON.parse(responseText)
      : undefined;

    const apiFetchResponse: ApiFetchResponse<ResponseBody> = {
      data: responseJsonOrUndefined as ResponseBody,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      requestConfig: resolvedRequestConfig,
    };

    return apiFetchResponse;
  }

  async delete<ResponseBody = unknown>(
    path: string,
    options?: ApiClientRequestOptions,
  ): Promise<ApiFetchResponse<ResponseBody>> {
    const builtUrl = new URL(path, this.baseUrl);

    const requestConfig: RequestConfig = {
      url: builtUrl.toString(),
      headers: options?.headers,
    };

    const resolvedRequestConfig = await this.resolveRequestConfig(requestConfig);

    const response = await fetcher.delete(resolvedRequestConfig.url, {
      headers: resolvedRequestConfig.headers,
      signal: this.resolveSignal(options),
    });

    if (!response.ok) {
      throw new HttpException(response.status, response.statusText, await readErrorBody(response));
    }

    const responseText = await response.text();
    const responseHasJsonBody = responseText.length > 0;

    const responseJsonOrUndefined: ResponseBody | undefined = responseHasJsonBody
      ? JSON.parse(responseText)
      : undefined;

    const apiFetchResponse: ApiFetchResponse<ResponseBody> = {
      data: responseJsonOrUndefined as ResponseBody,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      requestConfig: resolvedRequestConfig,
    };

    return apiFetchResponse;
  }

  async addRequestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor);
  }

  private resolveSignal(options?: ApiClientRequestOptions): AbortSignal | undefined {
    return options?.signal ?? createTimeoutSignal(this.defaultTimeoutMs);
  }

  private async resolveRequestConfig(requestConfig: RequestConfig): Promise<RequestConfig> {
    let currentRequestConfig = requestConfig;

    for (const interceptor of this.requestInterceptors) {
      currentRequestConfig = await interceptor(currentRequestConfig);
    }

    return currentRequestConfig;
  }
}
